import { useEffect, useState, useMemo } from "react";
import { Checkbox, Button1, QuickActionSelect } from "../index";
import ManageMySpending, { currentMonthOption } from "../manage-spending/ManageMySpending";
import CreateTransactionModal from "../modals/CreateTransactionModal";
import { DatePicker, Input } from "antd";
import calendarIcon from "../../assets/icons/calendar-icon.png";
import Table from "../table/Table";
import { useTransactions, useAccounts } from "../../data/live";
import bankIcon from "../../assets/icons/bank.png";
import walletIcon from "../../assets/icons/wallet-icon.png";
import cardIcon from "../../assets/icons/credit-card.png";

const formatCurrency = (value) =>
  Number(value).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

// Reverted/never-settled transfers keep the money in the account, so they
// must not count toward the cash-flow totals even though they stay in history.
const REVERTED_STATUSES = new Set(["FAILED", "CANCELLED", "REVERSED"]);
const countsAsMoved = (t) => !REVERTED_STATUSES.has(String(t.status || "").toUpperCase());

// Aggregate transactions into Credits/Debits/Net
const buildCashFlow = (transactions) => {
  const settled = transactions.filter(countsAsMoved);
  const credits = settled
    .filter((t) => t.direction === "in")
    .reduce((sum, t) => sum + t.amount, 0);
  const debits = settled
    .filter((t) => t.direction === "out")
    .reduce((sum, t) => sum + t.amount, 0);
  const net = credits - debits;
  return [
    {
      name: "Credits",
      color: "#1bbb6b",
      amount: `+$${formatCurrency(credits)}`,
    },
    {
      name: "Debits",
      color: "#FC555B",
      amount: `-$${formatCurrency(debits)}`,
    },
    {
      name: "Net",
      color: net >= 0 ? "var(--accent-color)" : "#FC555B",
      amount: `${net >= 0 ? "+" : "-"}$${formatCurrency(Math.abs(net))}`,
    },
  ];
};

const MyTransaction = () => {
  const { items: transactionList, refresh: refreshTransactions } = useTransactions();
  const { accounts } = useAccounts();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(currentMonthOption());

  // Filter states
  const [selectedAccountIds, setSelectedAccountIds] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [descriptionQuery, setDescriptionQuery] = useState("");
  const [amountMin, setAmountMin] = useState("");
  const [amountMax, setAmountMax] = useState("");
  const [selectedTypes, setSelectedTypes] = useState([]); // ["in"|"out"]
  const [sortKey, setSortKey] = useState("dueDate");
  const [sortDirection, setSortDirection] = useState("desc");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);
  const [selectedIds, setSelectedIds] = useState([]);

  // Which filter panels are open
  const [open, setOpen] = useState({
    category: true,
    subcategory: false,
    description: false,
    tags: false,
    amount: false,
    type: false,
    accounts: true,
  });
  const toggle = (k) => setOpen((p) => ({ ...p, [k]: !p[k] }));

  const filteredTransactions = useMemo(() => {
    return transactionList.filter((t) => {
      if (selectedMonth) {
        const [mShort, yShort] = selectedMonth.split("-");
        const monthMap = {
          JAN: 0, FEB: 1, MAR: 2, APR: 3, MAY: 4, JUN: 5,
          JUL: 6, AUG: 7, SEP: 8, OCT: 9, NOV: 10, DEC: 11,
        };
        const targetMonth = monthMap[mShort.toUpperCase()];
        const targetYear = 2000 + parseInt(yShort);
        const txDate = new Date(t.raw?.createdAt || t.date);
        if (txDate.getMonth() !== targetMonth || txDate.getFullYear() !== targetYear) {
          return false;
        }
      }

      if (selectedAccountIds.length > 0 && !selectedAccountIds.includes(t.raw?.sourceAccountId)) {
        return false;
      }
      if (selectedCategories.length > 0 && !selectedCategories.includes(t.category)) {
        return false;
      }
      if (descriptionQuery.trim()) {
        const q = descriptionQuery.trim().toLowerCase();
        const hay = `${t.counterparty || ""} ${t.type || ""} ${t.raw?.reference_note || ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      if (amountMin !== "" && Number(t.amount) < Number(amountMin)) return false;
      if (amountMax !== "" && Number(t.amount) > Number(amountMax)) return false;
      if (selectedTypes.length > 0 && !selectedTypes.includes(t.direction)) return false;

      return true;
    });
  }, [
    transactionList,
    selectedMonth,
    selectedAccountIds,
    selectedCategories,
    descriptionQuery,
    amountMin,
    amountMax,
    selectedTypes,
  ]);

  const cashFlow = buildCashFlow(filteredTransactions);
  // Only settled money movements feed the cash-flow summary header.
  const settledTransactions = filteredTransactions.filter(countsAsMoved);

  const bankAccounts = accounts.filter(a => a.category === "Personal Wallet" || a.category === "Wallet");
  const creditCards = accounts.filter(a => a.category === "Credit Card");
  const loans = accounts.filter(a => a.category === "Loans");
  const investments = accounts.filter(a => a.category === "Investment");

  const categories = Array.from(new Set(transactionList.map(t => t.category).filter(Boolean)));

  const handleAccountToggle = (id) => {
    setSelectedAccountIds(prev => 
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const handleCategoryToggle = (cat) => {
    setSelectedCategories(prev => 
      prev.includes(cat) ? prev.filter(c => c !== cat) : [...prev, cat]
    );
  };

  const handleReset = () => {
    setSelectedAccountIds([]);
    setSelectedCategories([]);
    setSelectedMonth(currentMonthOption());
    setDescriptionQuery("");
    setAmountMin("");
    setAmountMax("");
    setSelectedTypes([]);
  };

  const toggleType = (dir) =>
    setSelectedTypes((p) => (p.includes(dir) ? p.filter((d) => d !== dir) : [...p, dir]));

  const myTransactionRows = useMemo(
    () =>
      filteredTransactions.map((t, index) => {
        const dueDate = t.date.split(" at ")[0];
        const bill =
          t.type === "Receive Money" || t.type === "DEPOSIT"
            ? `From ${t.counterparty}`
            : `${t.type}, ${t.counterparty}`;
        const subCategory = t.category || "Uncategorized";
        const asBill = t.type === "Bill Payment" ? "Yes" : "No";
        const amount = `${t.direction === "out" ? "-" : "+"}${formatCurrency(
          t.amount
        )} ${t.currency}`;

        return {
          id: t.id || t.raw?.id || `${dueDate}-${bill}-${t.amount}-${index}`,
          dueDate,
          dueDateValue: new Date(t.raw?.createdAt || t.date).getTime() || 0,
          bill,
          subCategory,
          asBill,
          amount,
          amountValue: Number(t.amount) || 0,
          currency: t.currency,
          direction: t.direction,
        };
      }),
    [filteredTransactions]
  );

  const sortedTransactions = useMemo(() => {
    const getValue = (row) => {
      if (sortKey === "dueDate") return row.dueDateValue;
      if (sortKey === "amount") return row.amountValue;
      return row[sortKey] || "";
    };

    return [...myTransactionRows].sort((a, b) => {
      const first = getValue(a);
      const second = getValue(b);
      const result =
        typeof first === "number" && typeof second === "number"
          ? first - second
          : String(first).localeCompare(String(second), undefined, { numeric: true });
      return sortDirection === "asc" ? result : -result;
    });
  }, [myTransactionRows, sortDirection, sortKey]);

  const totalPages = Math.max(1, Math.ceil(sortedTransactions.length / pageSize));

  useEffect(() => {
    setCurrentPage(1);
  }, [sortKey, sortDirection, pageSize]);

  useEffect(() => {
    setCurrentPage((page) => Math.min(page, totalPages));
  }, [totalPages]);

  useEffect(() => {
    setSelectedIds((prev) =>
      prev.filter((id) => sortedTransactions.some((row) => row.id === id))
    );
  }, [sortedTransactions]);

  const paginatedTransactions = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return sortedTransactions.slice(start, start + pageSize);
  }, [currentPage, pageSize, sortedTransactions]);

  const firstResult = sortedTransactions.length === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const lastResult = Math.min(currentPage * pageSize, sortedTransactions.length);
  const selectableIds = sortedTransactions.map((row) => row.id);
  const allSelected =
    selectableIds.length > 0 && selectableIds.every((id) => selectedIds.includes(id));
  const someSelected = selectedIds.length > 0 && !allSelected;

  const toggleAll = () => {
    setSelectedIds((prev) => {
      if (allSelected) return prev.filter((id) => !selectableIds.includes(id));
      return [...new Set([...prev, ...selectableIds])];
    });
  };

  const toggleOne = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const handleHeaderSort = (key) => {
    setSortKey(key);
    setSortDirection((direction) =>
      sortKey === key && direction === "asc" ? "desc" : "asc"
    );
  };

  const sortIcon = (key) => {
    if (sortKey !== key) return "↕";
    return sortDirection === "asc" ? "↑" : "↓";
  };

  const sortableHeader = (label, key) => (
    <button
      type="button"
      className="recipient-table-sort"
      onClick={() => handleHeaderSort(key)}
    >
      <span>{label}</span>
      <span>{sortIcon(key)}</span>
    </button>
  );

  const myTransactionTableHeader = [
    <Checkbox
      checked={allSelected}
      indeterminate={someSelected}
      onChange={toggleAll}
    />,
    sortableHeader("Due Date", "dueDate"),
    sortableHeader("Bills", "bill"),
    sortableHeader("Subcategory", "subCategory"),
    sortableHeader("As Bill", "asBill"),
    sortableHeader("Amount", "amount"),
  ];

  const myTransactionTableBody = paginatedTransactions.map((row) => ({
    ...row,
    selected: selectedIds.includes(row.id),
    onToggle: () => toggleOne(row.id),
  }));

  return (
    <>
      <ManageMySpending 
        navName={"My Transaction"} 
        selectedMonth={selectedMonth} 
        onMonthChange={setSelectedMonth}
        pageClassName="dashboard-page my-transaction-page"
        contentClassName="dashboard-content"
      >
        <div className="my-transaction-shell">
          <div className="my-transaction-overview">
            <div>
              <span className="dashboard-eyebrow">Money movement</span>
              <h2>Spending Report</h2>
              <p>Track cash flow, review transaction history, and refine the list with focused filters.</p>
            </div>
            <div className="d-flex gap-2 align-items-center flex-wrap justify-content-md-end">
              <Button1
                buttonName="New Transaction"
                className="btn-dark"
                onClick={() => setShowCreateModal(true)}
              />
              <QuickActionSelect className="rounded-5 change-down-icon-white" />
            </div>
          </div>

          <div className="row mt-2 g-4">
          <div className="col-lg-8 mt-3">
            <div
              className="my-transaction-card my-transaction-cashflow d-flex flex-column gap-3"
            >
              <div className="d-flex justify-content-between align-items-center">
                <span className="fw-semibold fs-5">Cash flow</span>
                <span
                  className="my-transaction-link cursor-pointer fw-semibold"
                >
                  Go to my account
                </span>
              </div>

              <div className="d-flex justify-content-md-between align-items-center flex-wrap flex-md-nowrap gap-md-0 gap-3 ">
                <div className="d-flex flex-column  gap-1">
                  <span className="fw-semibold fs-4">
                    {new Set(settledTransactions.map((t) => t.currency)).size}{" "}
                    {new Set(settledTransactions.map((t) => t.currency)).size === 1
                      ? "Account"
                      : "Accounts"}
                  </span>
                  <span
                    className="fs12px fw-medium"
                    style={{ color: "var(--text-muted)" }}
                  >
                    {settledTransactions.length} transactions
                  </span>
                </div>

                <div className="my-transaction-flow-metrics d-flex justify-content-around align-items-center p-3 rounded-3 tag-box-width-control ">
                  {cashFlow.map(({ color, amount, name }) => (
                    <div className="d-flex flex-column gap-1 align-items-center" key={name}>
                      <span
                        className="fs14px fw-medium"
                        style={{ color: color }}
                      >
                        {amount}
                      </span>
                      <span
                        className="fs12px fw-medium"
                        style={{ color: "var(--text-muted)" }}
                      >
                        {name}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="my-transaction-card d-flex flex-column gap-3 mt-4">
              <div className="my-transaction-card-header d-flex justify-content-between align-items-center p-3 px-4">
                <div>
                  <span className="fs-5 fw-semibold">My Transaction</span>
                  {selectedIds.length > 0 && (
                    <div className="fs12px mt-1" style={{ color: "var(--text-muted)" }}>
                      {selectedIds.length} selected
                    </div>
                  )}
                </div>
                <div className="my-transaction-controls d-flex align-items-center justify-content-end gap-2 w-50">
                  <DatePicker
                    className="my-transaction-date rounded-pill w-auto"
                    placeholder="Date From"
                    suffixIcon={<img src={calendarIcon} alt="icon" />}
                  />
                  <DatePicker
                    className="my-transaction-date rounded-pill w-auto"
                    placeholder="Date To"
                    suffixIcon={<img src={calendarIcon} alt="icon" />}
                  />
                </div>
              </div>

              <div className="px-4 p-3">
                <Table
                  MyTransactionTable={true}
                  tableBody={myTransactionTableBody}
                  tableHeaders={myTransactionTableHeader}
                  MyTransactionTags={[]}
                  className={"w-100"}
                />

                <div className="my-transaction-pagination recipient-pagination">
                  <span>
                    Showing {firstResult}-{lastResult} of {sortedTransactions.length}
                  </span>
                  <div className="d-flex align-items-center gap-2">
                    <select
                      value={pageSize}
                      onChange={(e) => setPageSize(Number(e.target.value))}
                      aria-label="Rows per page"
                    >
                      <option value={5}>5 / page</option>
                      <option value={10}>10 / page</option>
                      <option value={15}>15 / page</option>
                    </select>
                    <button
                      type="button"
                      onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
                      disabled={currentPage === 1}
                    >
                      Previous
                    </button>
                    <strong>
                      {currentPage} / {totalPages}
                    </strong>
                    <button
                      type="button"
                      onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))}
                      disabled={currentPage === totalPages}
                    >
                      Next
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-lg-4 mt-3">
            <div className="my-transaction-filter d-flex flex-column justify-content-between h-100">
              <div className="d-flex flex-column ">
                <div className="my-transaction-filter-header d-flex justify-content-between align-items-center p-3">
                  <span className="fs-5 fw-semibold">Filter</span>
                  <span
                    className="my-transaction-link fw-medium fs14px cursor-pointer"
                    onClick={handleReset}
                  >
                    Reset
                  </span>
                </div>

                {/* Category */}
                <div className="my-transaction-filter-group d-flex flex-column gap-2 p-3">
                  <div
                    className="d-flex justify-content-between align-items-center cursor-pointer"
                    onClick={() => toggle("category")}
                  >
                    <span className="fw-semibold">Category</span>
                    <div className="d-flex gap-2 align-items-center">
                      <span className="fs14px" style={{ color: "var(--text-muted)" }}>
                        {selectedCategories.length} Selected
                      </span>
                      <span className="fs-5 fw-bold" style={{ color: "var(--text-muted)" }}>
                        {open.category ? "−" : "+"}
                      </span>
                    </div>
                  </div>
                  {open.category && (
                    <div className="d-flex flex-column gap-2">
                      {categories.length === 0 ? (
                        <span className="fs12px" style={{ color: "var(--text-muted)" }}>
                          No categories available
                        </span>
                      ) : (
                        categories.map((name) => (
                          <div className="d-flex align-items-center gap-2" key={name}>
                            <Checkbox
                              checked={selectedCategories.includes(name)}
                              onChange={() => handleCategoryToggle(name)}
                            />
                            <span className="fs12px fw-medium">{name}</span>
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </div>

                {/* SubCategory */}
                <div className="my-transaction-filter-group d-flex flex-column gap-2 p-3">
                  <div
                    className="d-flex justify-content-between align-items-center cursor-pointer"
                    onClick={() => toggle("subcategory")}
                  >
                    <span className="fw-semibold">Subcategory</span>
                    <span className="fs-5 fw-bold" style={{ color: "var(--text-muted)" }}>
                      {open.subcategory ? "−" : "+"}
                    </span>
                  </div>
                  {open.subcategory && (
                    <span className="fs12px" style={{ color: "var(--text-muted)" }}>
                      No subcategories available for the current data set.
                    </span>
                  )}
                </div>

                {/* Description */}
                <div className="my-transaction-filter-group d-flex flex-column gap-2 p-3">
                  <div
                    className="d-flex justify-content-between align-items-center cursor-pointer"
                    onClick={() => toggle("description")}
                  >
                    <span className="fw-semibold">Description</span>
                    <span className="fs-5 fw-bold" style={{ color: "var(--text-muted)" }}>
                      {open.description ? "−" : "+"}
                    </span>
                  </div>
                  {open.description && (
                    <Input
                      placeholder="Search counterparty or note"
                      value={descriptionQuery}
                      onChange={(e) => setDescriptionQuery(e.target.value)}
                      allowClear
                    />
                  )}
                </div>

                {/* Tags */}
                <div className="my-transaction-filter-group d-flex flex-column gap-2 p-3">
                  <div
                    className="d-flex justify-content-between align-items-center cursor-pointer"
                    onClick={() => toggle("tags")}
                  >
                    <span className="fw-medium">Tags</span>
                    <span className="fs-5 fw-bold" style={{ color: "var(--text-muted)" }}>
                      {open.tags ? "−" : "+"}
                    </span>
                  </div>
                  {open.tags && (
                    <span className="fs12px" style={{ color: "var(--text-muted)" }}>
                      Tag-based filtering will appear once tags are attached to transactions.
                    </span>
                  )}
                </div>

                {/* Amount */}
                <div className="my-transaction-filter-group d-flex flex-column gap-2 p-3">
                  <div
                    className="d-flex justify-content-between align-items-center cursor-pointer"
                    onClick={() => toggle("amount")}
                  >
                    <span className="fw-semibold">Amount</span>
                    <span className="fs-5 fw-bold" style={{ color: "var(--text-muted)" }}>
                      {open.amount ? "−" : "+"}
                    </span>
                  </div>
                  {open.amount && (
                    <div className="d-flex gap-2">
                      <Input
                        type="number"
                        placeholder="Min"
                        value={amountMin}
                        onChange={(e) => setAmountMin(e.target.value)}
                      />
                      <Input
                        type="number"
                        placeholder="Max"
                        value={amountMax}
                        onChange={(e) => setAmountMax(e.target.value)}
                      />
                    </div>
                  )}
                </div>

                {/* Transaction Type */}
                <div className="my-transaction-filter-group d-flex flex-column gap-2 p-3">
                  <div
                    className="d-flex justify-content-between align-items-center cursor-pointer"
                    onClick={() => toggle("type")}
                  >
                    <span className="fw-semibold">Transaction Type</span>
                    <span className="fs-5 fw-bold" style={{ color: "var(--text-muted)" }}>
                      {open.type ? "−" : "+"}
                    </span>
                  </div>
                  {open.type && (
                    <div className="d-flex flex-column gap-2">
                      <div className="d-flex align-items-center gap-2">
                        <Checkbox
                          checked={selectedTypes.includes("in")}
                          onChange={() => toggleType("in")}
                        />
                        <span className="fs12px fw-medium">Credits (Money In)</span>
                      </div>
                      <div className="d-flex align-items-center gap-2">
                        <Checkbox
                          checked={selectedTypes.includes("out")}
                          onChange={() => toggleType("out")}
                        />
                        <span className="fs12px fw-medium">Debits (Money Out)</span>
                      </div>
                    </div>
                  )}
                </div>

                {/* Accounts */}
                <div className="my-transaction-filter-group d-flex flex-column gap-2 p-3 ">
                  <div
                    className="d-flex justify-content-between align-items-center cursor-pointer"
                    onClick={() => toggle("accounts")}
                  >
                    <span className="fw-medium">Accounts</span>
                    <div className="d-flex gap-2 align-items-center">
                      <span className="fs14px" style={{ color: "var(--text-muted)" }}>
                        {selectedAccountIds.length} Selected
                      </span>
                      <span className="fs-5 fw-bold" style={{ color: "var(--text-muted)" }}>
                        {open.accounts ? "−" : "+"}
                      </span>
                    </div>
                  </div>

                  {open.accounts && bankAccounts.length > 0 && (
                    <div className="d-flex flex-column gap-2">
                      <span className="fs14px" style={{ color: "var(--text-muted)" }}>
                        Bank Accounts
                      </span>
                      {bankAccounts.map((acc) => (
                        <div key={acc.id} className="d-flex gap-2 align-items-center">
                          <Checkbox 
                            checked={selectedAccountIds.includes(acc.id)}
                            onChange={() => handleAccountToggle(acc.id)}
                          />
                          <img src={walletIcon} width={20} height={20} alt="icon" />
                          <span className="fs12px fw-medium">{acc.currency} Wallet</span>
                        </div>
                      ))}
                    </div>
                  )}

                  {open.accounts && creditCards.length > 0 && (
                    <div className="d-flex flex-column gap-2 mt-2">
                      <span className="fs14px" style={{ color: "var(--text-muted)" }}>
                        Credit Card
                      </span>
                      {creditCards.map((acc) => (
                        <div key={acc.id} className="d-flex gap-2 align-items-center">
                          <Checkbox
                            checked={selectedAccountIds.includes(acc.id)}
                            onChange={() => handleAccountToggle(acc.id)}
                          />
                          <img src={cardIcon} width={20} height={20} alt="icon" />
                          <span className="fs12px fw-medium">{acc.currency} Card</span>
                        </div>
                      ))}
                    </div>
                  )}

                  {open.accounts && loans.length > 0 && (
                    <div className="d-flex flex-column gap-2 mt-2">
                      <span className="fs14px" style={{ color: "var(--text-muted)" }}>
                        Loans
                      </span>
                      {loans.map((acc) => (
                        <div key={acc.id} className="d-flex gap-2 align-items-center">
                          <Checkbox
                            checked={selectedAccountIds.includes(acc.id)}
                            onChange={() => handleAccountToggle(acc.id)}
                          />
                          <img src={bankIcon} width={20} height={20} alt="icon" />
                          <span className="fs12px fw-medium">{acc.currency} Loan</span>
                        </div>
                      ))}
                    </div>
                  )}

                  {open.accounts && investments.length > 0 && (
                    <div className="d-flex flex-column gap-2 mt-2">
                      <span className="fs14px" style={{ color: "var(--text-muted)" }}>
                        Investment
                      </span>
                      {investments.map((acc) => (
                        <div key={acc.id} className="d-flex gap-2 align-items-center">
                          <Checkbox
                            checked={selectedAccountIds.includes(acc.id)}
                            onChange={() => handleAccountToggle(acc.id)}
                          />
                          <img src={bankIcon} width={20} height={20} alt="icon" />
                          <span className="fs12px fw-medium">{acc.currency} Invest</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
        </div>
      </ManageMySpending>
      <CreateTransactionModal
        open={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onCreate={(data) => {
          console.log("Transaction Created:", data);
          refreshTransactions();
        }}
      />
    </>
  );
};

export default MyTransaction;
